import React, { useEffect, useState } from "react";
import { GLOBAL_IMAGES, HEADER } from "../../assets/asset";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleMute } from "../../store/slice/muteSlice";
import AIModal from "../HandleAIQuestions/AIModal";
import SubscribedBadge from "../subscription/SubscribedBadge";
import { useSubscription } from "../../context/SubscriptionContext";
import PwaInstallTrigger from "../InstallPrompt/PwaInstallTrigger";

const Header = ({
  onLogout,
  showDemo,
  showLessonContent,
  showClassBooking = true,
}) => {
  const { user } = useSelector((state) => state.user);
  const { isMuted } = useSelector((state) => state.mute);
  const { showKnowledge } = useSelector((state) => state.profile);
  const { effectiveStatus, normalizedPlanType, hasAccess } = useSubscription();
  const [showAboutInfo, setShowAboutInfo] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showAiLockedPopup, setShowAiLockedPopup] = useState(false);
  const dispatch = useDispatch();
  const hasActiveOrTrialSubscription =
    effectiveStatus === "trialing" || effectiveStatus === "active";
  const isAiLocked =
    normalizedPlanType === "basic" && (hasActiveOrTrialSubscription || hasAccess);
  const canShowClassBooking =
    hasActiveOrTrialSubscription || hasAccess;

  const navigate = useNavigate();
  
  const handleAIClick = () => {
    if (isAiLocked) {
      setShowAiLockedPopup(true);
      return;
    }
    // Open the modal first
    setShowAIModal(true);
  };
  
  const handleGetStarted = () => {
    // Close the modal and navigate to AIHandler
    setShowAIModal(false);
    const yearId = user?.YearId || "7";
    navigate(`/AIHandler/${yearId}`);
  };

  useEffect(() => {
    if (showAboutInfo) {
      const timeoutID = setTimeout(() => {
        setShowAboutInfo(false);
      }, 3000);
      return () => clearTimeout(timeoutID);
    }
  }, [showAboutInfo]);

  const handleAboutButtonClick = () => {
    setShowAboutInfo(true);
  };

  const toggleSound = () => {
    dispatch(toggleMute());
  };

  return (
    <>
      <div className="flex justify-between items-center w-full py-4 px-6">
        <div className="flex items-center gap-3">
          <img
            src={GLOBAL_IMAGES.dingo}
            alt="MathDingo Logo"
            className="w-16 h-16 md:w-24 md:h-24 lg:w-32 lg:h-32 object-contain flex-shrink-0"
          />
          <PwaInstallTrigger mode="icon" />
        </div>

        {/* Middle Section - Icons */}
        <div className="flex items-center space-x-3 md:space-x-4 lg:space-x-6">
          {showClassBooking && canShowClassBooking && (
            <Link
              to="/classBooking/dashboard"
              className="inline-flex items-center rounded-md border-[3px] border-[#0B2B66] bg-gradient-to-r from-[#B3F5FF] via-[#7BE1FF] to-[#57C7FF] px-4 py-1.5 shadow-[0_4px_0_#0B2B66]"
              aria-label="Go to class booking"
            >
              <span className="font-extrabold text-[#0B2B66] text-xs md:text-sm uppercase tracking-[0.08em]">
                Class Booking
              </span>
            </Link>
          )}
          <SubscribedBadge />
          <Link to="/report">
            <img src={HEADER.ReportCard} alt="Report Card" className="w-10 h-10 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain flex-shrink-0" />
          </Link>

          <Link to="/leaderboard">
            <img src={GLOBAL_IMAGES.medal} alt="Leaderboard" className="w-10 h-10 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain flex-shrink-0" />
          </Link>

          <img
            src={isMuted ? HEADER.muteSound : HEADER.Sound}
            onClick={toggleSound}
            className="w-10 h-10 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain flex-shrink-0 cursor-pointer"
          />

          <div className="year-about-info-wrap relative">
            <img
              src={HEADER.About}
              alt="About"
              className="year-about-info-icon w-10 h-10 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain flex-shrink-0"
              onClick={handleAboutButtonClick}
            />

            {showAboutInfo && (
              <div
                className="year-about-info-bubble absolute bg-primary-brown p-2 rounded-lg text-sm right-0 text-white w-96 bubble"
              >
                <p className="year-about-info-text">
                  Stay tuned for exciting updates ahead! Our syllabus follows the
                  VIC curriculum for top-quality education.
                </p>
              </div>
            )}
          </div>

          {/* User Profile Icon */}
          <Link to={`/settings`}>
            <img
              src={user?.profilephotos}
              alt="User Profile"
              className={`w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full object-cover flex-shrink-0 ${showDemo === 1 ? "relative z-40" : ""}`}
            />
          </Link>

          {/*  on click open AI Modal first */}
          <button
            type="button"
            onClick={handleAIClick}
            aria-disabled={isAiLocked}
            title={isAiLocked ? "Upgrade to Pro to unlock AI." : undefined}
            aria-label={
              isAiLocked
                ? "AI assistant locked on Basic plan"
                : "Open AI assistant"
            }
            className={`relative flex items-center justify-center bg-transparent p-0 border-0 ${
              isAiLocked ? "cursor-pointer" : "cursor-pointer"
            } ${
              showDemo === 2 || showDemo === 1 || showLessonContent
                ? "z-10"
                : "z-30"
            }`}
          >
            <img
              src={HEADER.AI}
              alt=""
              aria-hidden="true"
              className="w-12 h-12 md:w-20 md:h-20 lg:w-28 lg:h-28 object-contain flex-shrink-0"
            />
            {isAiLocked && (
              <img
                src={GLOBAL_IMAGES.lesson_lock}
                alt=""
                aria-hidden="true"
                className="absolute bottom-1 right-0 h-10 w-10"
                data-testid="ai-lock-icon"
              />
            )}
          </button>
        </div>
      </div>
      
      {/* AI Modal */}
      <AIModal 
        isOpen={showAIModal} 
        onClose={() => setShowAIModal(false)} 
        onGetStarted={handleGetStarted} 
      />

      {showAiLockedPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071733]/65 px-4 backdrop-blur-md">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-lock-popup-title"
            className="w-full max-w-lg rounded-3xl border-4 border-[#0B2B66] bg-[linear-gradient(180deg,rgba(255,251,240,0.98),rgba(255,245,222,0.96))] px-6 py-7 text-center shadow-[0_20px_50px_rgba(7,23,51,0.38)]"
          >
            <h2
              id="ai-lock-popup-title"
              className="text-2xl font-extrabold text-[#0B2B66]"
            >
              AI Dingo is available on Pro
            </h2>
            <p className="mt-4 text-base font-semibold leading-7 text-[#5C4A2A]">
              It is accessible for pro users only. Unlock the full potential of
              AI Dingo by upgrading to the Pro version. Get access to AI
              features and enhanced learning experiences.
            </p>
            <button
              type="button"
              onClick={() => setShowAiLockedPopup(false)}
              className="mt-6 rounded-xl border-2 border-[#0B2B66] bg-[#0B2B66] px-6 py-2.5 font-bold text-white"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
